import { useQueryClient } from '@tanstack/react-query';
import { Button, Dropdown, Modal, Spin } from 'antd';
import apiCall from 'apps/investment/apiCalls/order';
import {
  orderStatusCancelled,
  orderStatusCompleted,
  orderProgramTypeFlex,
} from 'apps/investment/constants/fundOrder';
import { usePostApi } from 'hooks/api';
import { useEffect, useState } from 'react';

export const FundOrderAction = ({ record }) => {
  const [fundOrderID, setFundOrderID] = useState(record.fund_order_id);
  const [isForceCancelModalOpen, setIsForceCancelModalOpen] = useState(false);
  const queryClient = useQueryClient();

  // SubmitFundOrder, SubmitCancel and ForceCancel all persist synchronously
  // (FundOrderService.UpdateFundOrder) before the handler returns, so a refetch
  // shows the new status straight away.
  const refreshOrders = () => {
    queryClient.invalidateQueries({ queryKey: [apiCall.list.queryKey] });
    queryClient.invalidateQueries({ queryKey: [apiCall.detail.queryKey] });
  };

  const {
    data: orderStatusData,
    post: syncOrderStatus,
    isLoading: isSyncOrderStatus,
    error: syncOrderStatusError,
  } = usePostApi(apiCall.sync, {
    resourceName: 'data',
    onSuccess: refreshOrders,
  });

  const {
    post: cancelOrder,
    isLoading: isCancelling,
    error: cancelOrderError,
  } = usePostApi(apiCall.cancel, {
    onSuccess: refreshOrders,
  });

  const {
    post: forceCancel,
    isLoading: isForceCancelling,
    error: forceCancelError,
  } = usePostApi(apiCall.forceCancel, {
    onSuccess: () => {
      setIsForceCancelModalOpen(false);
      refreshOrders();
    },
  });

  useEffect(() => {
    setFundOrderID(record.fund_order_id);
  }, [record.fund_order_id]);

  useEffect(() => {
    if (orderStatusData && !isSyncOrderStatus) {
      const { fund_order_id } = orderStatusData;
      setFundOrderID(fund_order_id);
    }
  }, [orderStatusData, isSyncOrderStatus]);

  const isLoading = isSyncOrderStatus || isCancelling || isForceCancelling;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  const isCancelledOrCompleted =
    record.status == orderStatusCancelled ||
    record.status == orderStatusCompleted;

  const isFlexOrder = record.program_type === orderProgramTypeFlex;

  const dropdownItems = [];

  if (!fundOrderID && !isCancelledOrCompleted) {
    dropdownItems.push({
      key: 'send-to-fund',
      label: 'Send to Fund',
      onClick: () =>
        confirm(
          `Are you sure you want to send the order #${record.code} to Fund Manager?`
        ) && syncOrderStatus({ id: record.id }),
    });

    if (isFlexOrder) {
      dropdownItems.push({
        key: 'cancel',
        label: 'Cancel Order',
        danger: true,
        onClick: () =>
          confirm(
            `Are you sure you want to cancel the order #${record.code}?`
          ) && cancelOrder({ id: record.id }),
      });
    }
  }

  if (!isCancelledOrCompleted) {
    dropdownItems.push({
      key: 'force-cancel',
      label: 'Force Cancel',
      danger: true,
      onClick: () => setIsForceCancelModalOpen(true),
    });
  }

  if (dropdownItems.length === 0) {
    return null;
  }

  return (
    <>
      <Dropdown menu={{ items: dropdownItems }} trigger={['click']}>
        <Button style={{ marginLeft: '10px' }}>Actions ▾</Button>
      </Dropdown>

      <Modal
        title="Force Cancel Order"
        open={isForceCancelModalOpen}
        onOk={() => forceCancel({ id: record.id })}
        onCancel={() => setIsForceCancelModalOpen(false)}
        okText="Confirm Force Cancel"
        okButtonProps={{ danger: true, loading: isForceCancelling }}
        cancelText="Cancel"
      >
        <p>
          Are you sure you want to <strong>force cancel</strong> order{' '}
          <strong>#{record.code}</strong>?
        </p>
        <p>
          This will bypass the normal status check and also cancel the
          associated transaction. This action cannot be undone.
        </p>
      </Modal>
    </>
  );
};
